/**
 * BankFeedDocumentDialog - host the existing CreateInvoicePage /
 * CreateBillPage / RecordOtherReceiptModal / RecordOtherPaymentModal
 * inside the Bank Transactions Center so the user never leaves the page.
 *
 * Why this exists
 * ---------------
 * Until iter 721 the Bank Feed "Create Document" flow used
 * `navigate('/invoices/new', { state: { bankFeedPrefill } })` which
 * teleported the user out of the bank-feed page.  That broke their
 * reconciliation context - they lost the row they were working on,
 * lost the bulk-selection state, and had to navigate back manually.
 *
 * This component wraps the SAME pages in a fullscreen Dialog and
 * passes them `embedded={true}` + an `onComplete` callback that
 * closes the dialog and refreshes the bank-feed rows in place.
 */
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { X } from 'lucide-react';
import CreateInvoicePage from '../../pages/CreateInvoicePage';
import CreateBillPage from '../../pages/CreateBillPage';

const TITLES = {
  invoice: { title: 'Create Invoice', subtitle: 'Recording an inflow from this bank transaction.' },
  bill:    { title: 'Record Bill / Expense', subtitle: 'Recording an outflow against this bank transaction.' },
};

export const BankFeedDocumentDialog = ({
  open,
  onOpenChange,
  docType,         // 'invoice' | 'bill'
  prefillData,     // bankFeedPrefill shape (amount, date, counterparty, etc.)
  onSaved,         // optional: called with no args when the doc is saved
}) => {
  if (!open || !docType) return null;
  const meta = TITLES[docType] || TITLES.invoice;

  const handleComplete = () => {
    onSaved?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[1200px] w-[96vw] h-[92vh] p-0 gap-0 flex flex-col overflow-hidden"
        data-testid={`bankfeed-doc-dialog-${docType}`}
      >
        {/* Header bar */}
        <div className="flex items-start justify-between border-b px-6 py-4 bg-background shrink-0">
          <div>
            <DialogTitle className="text-xl">{meta.title}</DialogTitle>
            <DialogDescription className="text-sm">{meta.subtitle}</DialogDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            data-testid="bankfeed-doc-dialog-close"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Scrollable body - hosts the full Create*Page form */}
        <ScrollArea className="flex-1 bg-muted/20">
          <div className="bg-background">
            {docType === 'invoice' && (
              <CreateInvoicePage
                embedded={true}
                prefillOverride={prefillData}
                onComplete={handleComplete}
                onCancel={() => onOpenChange(false)}
              />
            )}
            {docType === 'bill' && (
              <CreateBillPage
                embedded={true}
                prefillOverride={prefillData}
                onComplete={handleComplete}
                onCancel={() => onOpenChange(false)}
              />
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default BankFeedDocumentDialog;
